import { HTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * Card — DESIGN_SYSTEM.md §2.1
 * Default: bg-surface border-line radius-2xl.
 * Variants: default | inset (no border, bg-surface-2) | gradient (mobile hero).
 *
 * Subcomponents: Card.Head, Card.Body, Card.Foot. Legacy aliases
 * CardHeader/CardContent/CardFooter/CardTitle/CardDescription are kept.
 */

const cardVariants = cva('rounded-2xl', {
  variants: {
    variant: {
      default:  'bg-surface border border-line shadow-card',
      inset:    'bg-surface-2',
      gradient: 'bg-gradient-to-br from-ink to-brand-700 text-white shadow-hero',
    },
  },
  defaultVariants: { variant: 'default' },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const CardRoot = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  ),
);
CardRoot.displayName = 'Card';

const CardHead = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-start justify-between gap-3 px-card-x pt-5 pb-3', className)}
      style={{ paddingLeft: '1.375rem', paddingRight: '1.375rem' }}
      {...props}
    />
  ),
);
CardHead.displayName = 'Card.Head';

const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-[1.375rem] pb-5', className)}
      {...props}
    />
  ),
);
CardBody.displayName = 'Card.Body';

const CardFoot = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('border-t border-line px-[1.375rem] py-4', className)}
      {...props}
    />
  ),
);
CardFoot.displayName = 'Card.Foot';

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-h2 text-ink', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('mt-1 text-body-sm text-soft', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

// ── Legacy shadcn aliases (used widely; kept for compat) ────────────────
const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4 border-b border-line', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-6 py-4 bg-surface-2 border-t border-line rounded-b-2xl', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

const Card = Object.assign(CardRoot, {
  Head: CardHead,
  Body: CardBody,
  Foot: CardFoot,
});

export {
  Card,
  CardHead,
  CardBody,
  CardFoot,
  CardTitle,
  CardDescription,
  // Legacy
  CardHeader,
  CardContent,
  CardFooter,
};
